import React, { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import newLogoBlinketMobile from 'assets/images/blinket-mobile.svg';
import newLogoBlinket from 'assets/images/new-blinket-logo.svg';
import SupportChat from 'components/Container/PrivateLayout/SupportChat';
import { useAuthTokens } from 'hooks/useAuthTokens';
import { useAuthUser } from 'hooks/useAuthUser';
import loginService from 'services/login';
import userService from 'services/user/index';
import { BLINKET_APP_URL, BLINKET_TICKETS_APP_URL, WEBHOOK_URL } from 'settings';

import { ThemeProvider } from '@eduzz/ui-antd-theme';
import Layout from '@eduzz/ui-layout';

import { useLoginAsEmailMutation } from './hooks';
import styles from './styles.module.css';

const { Topbar, Content, Sidebar } = Layout;
const { Item, Group } = Sidebar;
const { UserMenu, UserMenuItem } = Topbar;

interface PrivateLayoutProps {
  children: React.ReactNode;
}

const PrivateLayout = ({ children }: PrivateLayoutProps) => {
  const location = useLocation();
  const { data: userData } = useAuthUser();
  const { data: storedToken } = useAuthTokens();
  const loginAsEmailMutation = useLoginAsEmailMutation();

  const isProducer = !!userData?.is_producer;

  useEffect(() => {
    if (!storedToken?.token) {
      loginService.auth();
    }
  }, [storedToken?.token]);

  useEffect(() => {
    if (userData?.id) {
      window.gtag('set', 'user_id', userData?.id);
    }
  }, [userData?.id]);

  const handleLogout = () => {
    if (localStorage.getItem('isSupport')) {
      localStorage.removeItem('isSupport');
    }

    userService.logout();
  };

  const loginByEmail = (email: string) => {
    loginAsEmailMutation.mutate(email);
  };

  return (
    <ThemeProvider>
      <Layout>
        <Topbar
          currentApplication='blinket'
          user={{
            name: userData?.user_name,
            belt: userData?.belt,
            avatar: userData?.photo
          }}
          logo={newLogoBlinket}
          logoMobile={newLogoBlinketMobile}
          id='eduzz-topbar-menu'
        >
          <UserMenu id='eduzz-topbar-menu-user'>
            <UserMenuItem
              id='eduzz-topbar-menu-shopping'
              as={NavLink}
              to={{
                pathname: `${BLINKET_TICKETS_APP_URL}/?utm_source=blinket&utm_medium=produtor`
              }}
              target='_blank'
              rel='noopener noreferrer'
              className={styles.userItem}
            >
              Eventos que comprei
            </UserMenuItem>
            <UserMenuItem
              id='eduzz-topbar-menu-marketplace'
              as={NavLink}
              to={{
                pathname: `${BLINKET_APP_URL}/marketplace`
              }}
              target='_blank'
              rel='noopener noreferrer'
              className={styles.userItem}
            >
              Marketplace
            </UserMenuItem>
            <>
              {!!userData?.acpo_users &&
                userData.acpo_users
                  .filter(otherUser => otherUser.email != userData.user_email)
                  .map(otherUser => (
                    <UserMenuItem
                      id={`eduzz-topbar-menu-acpo-${encodeURIComponent(otherUser.email)}`}
                      key={otherUser.email}
                      onClick={() => loginByEmail(otherUser.email)}
                      className={styles.userItem}
                    >
                      {otherUser.email}
                    </UserMenuItem>
                  ))}
            </>

            <UserMenuItem id='eduzz-topbar-menu-logout' onClick={handleLogout} className={styles.userItem}>
              Sair
            </UserMenuItem>
          </UserMenu>

          <SupportChat />
        </Topbar>

        <Sidebar currentLocation={location.pathname}>
          <Group id='eduzz-sidebar-group-home' label='Início' className={`${styles.sidebarTop} ${styles.sidebarGroup}`}>
            <Item id='eduzz-sidebar-resume' as={NavLink} to='/home' className={styles.sidebarItem}>
              Resumo
            </Item>
            <Item id='eduzz-sidebar-help-center' as={NavLink} to='/help-center' className={styles.sidebarItem}>
              Central de Ajuda
            </Item>
          </Group>
          <Group id='eduzz-sidebar-group-events' label='Eventos' className={styles.sidebarGroup}>
            <Item id='eduzz-sidebar-create-event' as={NavLink} to='/event' className={styles.sidebarItem}>
              Criar Evento
            </Item>
            {isProducer && (
              <Item id='eduzz-sidebar-my-events' as={NavLink} to='/my-events' className={styles.sidebarItem}>
                Meus Eventos
              </Item>
            )}
            <Item
              id='eduzz-sidebar-marketplace'
              as={NavLink}
              to={{
                pathname: `${BLINKET_APP_URL}/marketplace`
              }}
              target='_blank'
              isExternal={true}
              className={styles.sidebarItem}
            >
              Marketplace
            </Item>
          </Group>
          {isProducer && (
            <Group id='eduzz-sidebar-group-advanced' label='Avançado' className={styles.sidebarGroup}>
              <a
                id='eduzz-sidebar-webhook'
                href={`${WEBHOOK_URL}/webhook/configs`}
                target='_blank'
                rel='noreferrer'
                className='no-underline text-black'
              >
                <Item className={styles.sidebarItem}>Webhook</Item>
              </a>
            </Group>
          )}
        </Sidebar>
        <Content>
          <div className={styles.content}>{children}</div>
        </Content>
      </Layout>
    </ThemeProvider>
  );
};

export default PrivateLayout;
